import {
  Button as RawButton,
  ButtonOverrides,
  ButtonProps as ButtonBaseUIProps,
} from 'baseui/button';
import { Spinner } from 'baseui/spinner';
import { themedUseStyletron } from 'common/styles/styletron.theme';
import * as React from 'react';
import merge from 'ts-deepmerge';

export interface ButtonProps
  extends Omit<
    ButtonBaseUIProps,
    'endEnhancer' | 'startEnhancer' | 'children'
  > {
  buttonType?: 'primary' | 'secondary' | 'tertiary';
  variant?: 'normal' | 'medium' | 'small' | 'pill' | 'default';
  endEnhancer?: ((color: string, size: string) => React.ReactNode) | null;
  startEnhancer?: ((color: string, size: string) => React.ReactNode) | null;
  children?:
    | React.ReactNode
    | ((color: string, size: number) => React.ReactNode);
  justifyContent?: 'start' | 'center' | 'end';
  isError?: boolean;
  style?: React.CSSProperties;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  onKeyUp?: React.KeyboardEventHandler<HTMLButtonElement>;
}

export default function Button(props: ButtonProps) {
  const {
    variant = 'small',
    buttonType = 'primary',
    justifyContent = 'center',
    disabled,
    isError,
  } = props;
  const [, theme] = themedUseStyletron();

  const iconSize = React.useMemo<any>(() => {
    switch (variant) {
      case 'normal':
        return 24;
      case 'small':
      case 'pill':
        return 16;
      case 'medium':
        return 20;
      default:
        return 18;
    }
  }, [variant]);

  const iconTextColor = React.useMemo(() => {
    if (props.disabled) return theme.colors.skyLight;
    switch (buttonType) {
      case 'primary':
        return theme.colors.skyLightest;
      case 'secondary':
        return isError ? theme.colors.redDefault : theme.colors.primaryDefault;
      case 'tertiary':
        return isError ? theme.colors.redDefault : theme.colors.primaryDefault;
    }
  }, [buttonType, isError, props.disabled, theme.colors]);

  const fontStyle = React.useMemo(() => {
    switch (variant) {
      case 'normal':
        return theme.typography.body1Semibold;
      case 'medium':
        return theme.typography.body2Semibold;
      case 'small':
        return theme.typography.LabelSmall;
      case 'pill':
        return theme.typography.body3Semibold;
      default:
        return theme.typography.body3Regular;
    }
  }, [theme.typography, variant]);

  const variantStyle = React.useMemo<any>(() => {
    switch (variant) {
      case 'normal':
        return {
          padding: '16px 32px',
          borderRadius: '8px !important',
        };
      case 'medium':
        return {
          padding: '8px 24px',
          borderRadius: '8px !important',
        };
      case 'small':
        return {
          padding: '8px 24px',
          borderRadius: '8px !important',
        };
      case 'pill':
        return {
          padding: '8px 24px',
          borderRadius: '8px !important',
        };
      default:
        return {
          padding: '4px',
        };
    }
  }, [variant]);

  const rootStyle = React.useMemo<any>(() => {
    // if (disabled) {
    //   return {
    //     // background: theme.colors.skyLight,
    //     cursor: "disabled",
    //     border: "none !important",
    //     ":hover": {
    //       background: `${theme.colors.skyLight} !important`,
    //     },
    //     ":active": {
    //       background: `${theme.colors.skyLight} !important`,
    //     },
    //   };
    // }

    const obj = {
      ...(props.isLoading || disabled
        ? {
            border: 'none !important',
            cursor: 'not-allowed',
            background: theme.colors.skyLighter,

            ':hover': {
              background: theme.colors.skyLighter,
            },
            ':active': {
              background: theme.colors.skyLighter,
            },
          }
        : {}),
    };

    switch (buttonType) {
      case 'primary':
        return {
          background: isError
            ? theme.colors.redDefault
            : theme.colors.primaryDefault,
          border: `1px solid ${
            isError ? theme.colors.redDefault : theme.colors.primaryDefault
          }`,

          color: iconTextColor,
          ':hover': {
            background: isError
              ? theme.colors.redLight
              : theme.colors.primaryLight,
            border: `1px solid ${
              isError ? theme.colors.redLight : theme.colors.primaryLight
            }`,
          },
          ':active': {
            background: isError
              ? theme.colors.redDefault
              : theme.colors.primaryDefault,
            border: `1px solid ${
              isError ? theme.colors.redDefault : theme.colors.primaryDefault
            }`,
          },

          ...obj,
        };
      case 'secondary':
        return {
          background: theme.colors.white,
          border: `1px solid ${
            isError ? theme.colors.redDefault : theme.colors.primaryDefault
          }`,
          color: iconTextColor,

          ':hover': {
            background: isError
              ? theme.colors.redLightest
              : theme.colors.primaryLightest,
            border: `1px solid ${
              isError ? theme.colors.redDefault : theme.colors.primaryDefault
            }`,
          },
          ':active': {
            background: isError
              ? theme.colors.redLightest
              : theme.colors.primaryLightest,
            border: `1px solid ${
              isError ? theme.colors.redLight : theme.colors.primaryLight
            }`,
          },

          ...obj,
        };
      case 'tertiary':
        return {
          background: theme.colors.white,
          border: '1px solid transparent',
          color: iconTextColor,
          ':hover': {
            background: isError
              ? theme.colors.redLightest
              : theme.colors.primaryLightest,
            border: `1px solid ${
              isError ? theme.colors.redLightest : theme.colors.primaryLightest
            }`,
          },
          ':active': {
            background: isError
              ? theme.colors.redLighter
              : theme.colors.primaryLighter,
            border: `1px solid ${
              isError ? theme.colors.redLighter : theme.colors.primaryLighter
            }`,
          },

          ...obj,
        };
      default:
        return {};
    }
  }, [
    buttonType,
    disabled,
    iconTextColor,
    props.isLoading,
    theme.colors,
    isError,
  ]);

  const overrides: ButtonOverrides = merge(
    {
      Root: {
        style: {
          backgroundColor: 'none',
          ...fontStyle,
          ...rootStyle,
          ...variantStyle,
          ...props.style,
          justifyContent,
        },
      },
      StartEnhancer: {
        style: {
          marginRight: '8px',
        },
      },
      EndEnhancer: {
        style: {
          marginLeft: '8px',
        },
      },
    } as ButtonOverrides,
    { ...props.overrides },
  );

  return (
    <RawButton
      {...props}
      overrides={overrides}
      type={props.type || 'button'}
      endEnhancer={
        props.isLoading ? (
          <Spinner
            style={{ width: iconSize, height: iconSize }}
            $color={theme.colors.primaryDefault}
          />
        ) : (
          props.endEnhancer?.(iconTextColor, iconSize)
        )
      }
      disabled={props.isLoading || disabled}
      startEnhancer={
        props.isLoading ? null : props.startEnhancer?.(iconTextColor, iconSize)
      }
      isLoading={false}
      children={
        typeof props.children === 'function'
          ? props.children(iconTextColor, iconSize)
          : props.children
      }
    />
  );
}
